import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { noop } from 'lodash-es';
import { EMPTY_STATE_MESSAGE_STREAM, ROLE } from 'site-modules/canvas/constants/canvas';
import { Markdown } from 'site-modules/canvas/components/chat-messages/ai-message/markdown/markdown';
import { UserMessage } from 'site-modules/canvas/components/chat-messages/user-message/user-message';
import { AiMessage } from 'site-modules/canvas/components/chat-messages/ai-message/ai-message';
import { SuggestedMessages } from 'site-modules/canvas/components/chat-input/suggested-messages/suggested-messages';

import './chat-messages.scss';

export function ChatMessages({
  className = null,
  header = null,
  messages,
  setLoading,
  onLinkClick,
  onMCPLinkClick,
  customMarkdownComponents = null,
  onAiMessageComplete = noop,
  messagesContainerRef = null,
  messagesRef = null,
  onUserMessageRender: externalOnUserMessageRender = noop,
  onStreamStateChange = noop,
  onSubmit = noop,
  inputRef,
  suggestedMessage = null,
  isMobile = false,
}) {
  const userMessageRendererCallbacks = useMemo(
    () =>
      messages?.map((_, index) => userMessageRef => {
        const isLastUserMessage = !messages.slice(index + 1).some(message => message.role === ROLE.USER);

        externalOnUserMessageRender(userMessageRef, isLastUserMessage);
      }),
    [messages, externalOnUserMessageRender]
  );

  const markdownComponents = useMemo(
    () => ({
      h2({ children }) {
        return <p className="animate size-24 mb-2">{children}</p>;
      },
      p({ children }) {
        return <p className="animate size-20">{children}</p>;
      },
    }),
    []
  );
  return (
    <div
      ref={messagesContainerRef}
      className={classnames('chat-messages pos-r', className, {
        desktop: !isMobile,
      })}
    >
      {header}
      <div
        ref={messagesRef}
        className={classnames('messages-container d-flex flex-column gap-2', {
          'desktop w-100 mx-auto': !isMobile,
        })}
      >
        {messages?.map((message, index) => {
          switch (message.role) {
            case ROLE.ASSISTANT:
              return (
                <AiMessage
                  key={message.id}
                  isChatMessage
                  message={message}
                  setLoading={setLoading}
                  onLinkClick={onLinkClick}
                  onMCPLinkClick={onMCPLinkClick}
                  customMarkdownComponents={customMarkdownComponents}
                  setStreamState={onStreamStateChange}
                  onComplete={onAiMessageComplete}
                />
              );
            case ROLE.USER:
              return (
                <UserMessage
                  key={message.id}
                  isChatMessage
                  className="align-self-end"
                  message={message}
                  onRender={userMessageRendererCallbacks[index]}
                />
              );
            case ROLE.EMPTY_STATE:
              return (
                <div key={message.id} className="d-flex flex-column gap-1_25">
                  <Markdown
                    className="empty-state-message size-20 fw-bold"
                    bodyStream={EMPTY_STATE_MESSAGE_STREAM.default}
                    customComponents={markdownComponents}
                  />
                </div>
              );
            default:
              return null;
          }
        })}
        {!!suggestedMessage && (
          <div className="suggested-messages-wrapper">
            <SuggestedMessages
              key={suggestedMessage.id}
              message={suggestedMessage}
              onSubmit={onSubmit}
              inputRef={inputRef}
            />
          </div>
        )}
      </div>
    </div>
  );
}

ChatMessages.propTypes = {
  messages: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        role: PropTypes.oneOf(Object.values(ROLE)).isRequired,
        text: PropTypes.string,
      }),
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        role: PropTypes.oneOf(Object.values(ROLE)).isRequired,
        userText: PropTypes.string,
        responsePromise: PropTypes.instanceOf(Promise),
        isCanceled: PropTypes.bool,
      }),
    ])
  ),
  setLoading: PropTypes.func.isRequired,
  onLinkClick: PropTypes.func.isRequired,
  onMCPLinkClick: PropTypes.func.isRequired,
  customMarkdownComponents: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.string, PropTypes.func])),
  onAiMessageComplete: PropTypes.func,
  className: PropTypes.string,
  header: PropTypes.node,
  messagesContainerRef: PropTypes.shape({ current: PropTypes.shape({}) }),
  messagesRef: PropTypes.shape({ current: PropTypes.shape({}) }),
  onUserMessageRender: PropTypes.func,
  onStreamStateChange: PropTypes.func,
  onSubmit: PropTypes.func,
  inputRef: PropTypes.shape({}).isRequired,
  suggestedMessage: PropTypes.shape({
    id: PropTypes.string.isRequired,
    role: PropTypes.oneOf(Object.values(ROLE)).isRequired,
    userText: PropTypes.string,
    responsePromise: PropTypes.instanceOf(Promise),
    generatedResponseId: PropTypes.string,
  }),
  isMobile: PropTypes.bool,
};
